<template>
  <section class="dp-layout tr-page prep-library-page">
    <section class="dp-left">
      <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1"></DepartmentPane>
    </section>
    <section class="dp-right">
      <header class="tr-hero">
        <div>
          <p class="tr-eyebrow">集体备课 · 公共库</p>
          <h1 class="tr-title">教案公共库</h1>
          <p class="tr-sub">管理已发布教案的上架 / 下架，组员可在 H5 公共库查阅。</p>
        </div>
        <div class="tr-hero__meta">
          <div class="tr-stat">
            <span>当前结果</span>
            <strong>{{ total }}</strong>
          </div>
        </div>
      </header>

      <div class="tr-panel">
        <div class="tr-toolbar-wrap">
          <toolbar :buttonList="buttonList" @callFunction="callFunction" :searchList="searchList"></toolbar>
        </div>

        <div v-if="!schoolId" class="tr-empty">
          <div class="tr-empty__icon"><i class="el-icon-office-building"></i></div>
          <p class="tr-empty__title">尚未选择学校</p>
          <p class="tr-empty__desc">请从左侧机构树选择学校后再查阅教案公共库</p>
        </div>

        <template v-else>
          <!-- 列表：tbl-scroll-host + height 表体占满、空状态可见。最后更新：2026-08-31 -->
          <div class="tbl-scroll-host">
          <el-table height="100%" :data="tableData" v-loading="listLoading" class="tr-table" style="width: 100%">
            <el-table-column prop="Title" label="教案标题" min-width="160">
              <template slot-scope="scope">
                <span class="tr-name">{{ scope.row.Title }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="GroupName" label="教研组" min-width="120" />
            <el-table-column label="发布人" width="100">
              <template slot-scope="scope">{{ scope.row.PublishByName || (scope.row.PublishByTeacherMasterId ? "—" : "后台代发布") }}</template>
            </el-table-column>
            <el-table-column prop="PublishTime" label="发布时间" width="170" />
            <el-table-column label="状态" width="100" align="center">
              <template slot-scope="scope">
                <el-tag :type="scope.row.Status === 1 ? 'success' : 'info'" size="mini">
                  {{ scope.row.Status === 1 ? "已发布" : "已下架" }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="下架信息" min-width="160">
              <template slot-scope="scope">
                <span v-if="scope.row.Status === 2">
                  {{ scope.row.UnpublishByName || "后台代下架" }}
                  <span v-if="scope.row.UnpublishTime" class="tr-muted"> {{ scope.row.UnpublishTime }}</span>
                </span>
                <span v-else class="tr-muted">—</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="200" align="center" fixed="right">
              <template slot-scope="scope">
                <div class="table-op-pair">
                  <el-button
                    v-if="scope.row.Status === 1"
                    type="text"
                    size="small"
                    class="table-op-link table-op-link--danger"
                    @click="handleUnpublish(scope.row)"
                  >下架</el-button>
                  <el-button
                    v-else
                    type="text"
                    size="small"
                    class="table-op-link"
                    @click="handlePublish(scope.row)"
                  >代发布</el-button>
                  <el-button type="text" size="small" class="table-op-link" @click="goDocument(scope.row)">查看文档</el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>
          </div>

          <div class="tr-pager">
            <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="filters.Page"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="filters.PageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="total"
            />
          </div>
        </template>
      </div>
    </section>
  </section>
</template>

<script>
import DepartmentPane from "@/components/DepartmentPane";
import Toolbar from "@/components/Toolbar";
import { getButtonList } from "@/promissionRouter";
import {
  getLibraryPage,
  adminPublishToLibrary,
  adminUnpublishFromLibrary,
  getAllGroups,
  exportLibraryExcel
} from "@/api/modules/teaching-research";
import { normalizeDeptId, treeNodeDeptId } from "@/utils/deptCascaderProps";

const schoolLikeTypes = ["school", "campus", "affiliateschool"];

export default {
  name: "PrepLibrary",
  components: { DepartmentPane, Toolbar },
  data() {
    return {
      buttonList: [],
      schoolId: "",
      listLoading: false,
      tableData: [],
      total: 0,
      filters: {
        Page: 1,
        PageSize: 10,
        GroupId: null,
        Keyword: "",
        Status: 1
      },
      searchList: [
        { label: "教研组", key: "GroupId", type: "select", options: [], initValue: null },
        { label: "标题", key: "Keyword", type: "input", placeholder: "教案标题", initValue: "" },
        {
          label: "状态",
          key: "Status",
          type: "select",
          initValue: 1,
          options: [
            { label: "已发布", value: 1 },
            { label: "已下架", value: 2 },
            { label: "全部", value: null }
          ]
        }
      ]
    };
  },
  mounted() {
    this.buttonList = getButtonList(this.$route.path) || [];
  },
  methods: {
    treeNodeClick(data, node) {
      const schoolId = this.resolveSchoolId(node, treeNodeDeptId(data));
      if (!schoolId) {
        this.schoolId = "";
        this.tableData = [];
        return;
      }
      this.schoolId = schoolId;
      this.filters.Page = 1;
      this.loadGroups();
      this.getList();
    },
    /** 从树节点向上解析学校 Id（字符串）。最后修改：2026-10-18 */
    resolveSchoolId(node, fallback) {
      let n = node;
      while (n && n.data) {
        const t = String(n.data.DeptOrgType != null ? n.data.DeptOrgType : n.data.deptOrgType || "").toLowerCase();
        if (schoolLikeTypes.includes(t)) {
          return treeNodeDeptId(n.data) || normalizeDeptId(fallback);
        }
        n = n.parent;
      }
      return normalizeDeptId(fallback);
    },
    loadGroups() {
      if (!this.schoolId) return;
      getAllGroups({ schoolId: this.schoolId }).then(res => {
        if (!res.success) return;
        const opts = (res.response || []).map(g => ({ label: g.GroupName, value: g.Id }));
        const item = this.searchList.find(s => s.key === "GroupId");
        if (item) item.options = [{ label: "全部", value: null }, ...opts];
      });
    },
    callFunction(func, searchData) {
      if (func === "handleQuery" || func === "handleSearch") {
        if (searchData) {
          this.filters.GroupId = searchData.GroupId;
          this.filters.Keyword = searchData.Keyword || "";
          this.filters.Status = searchData.Status;
        }
        this.filters.Page = 1;
        this.getList();
      } else if (func === "exportExcel") {
        this.exportExcel();
      }
    },
    async exportExcel() {
      if (!this.schoolId) {
        this.$message.error("请先选择学校");
        return;
      }
      try {
        const response = await exportLibraryExcel({
          ...this.filters,
          SchoolId: this.schoolId
        });
        const blob = response && response.data instanceof Blob
          ? response.data
          : new Blob([response], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `教案公共库_${Date.now()}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.$message.success("导出成功");
      } catch (e) {
        this.$message.error("导出失败");
      }
    },
    getList() {
      if (!this.schoolId) return;
      this.listLoading = true;
      getLibraryPage({
        ...this.filters,
        SchoolId: this.schoolId
      })
        .then(res => {
          if (res.success) {
            this.tableData = (res.response && res.response.data) || [];
            this.total = (res.response && res.response.dataCount) || 0;
          } else {
            this.$message.error(res.msg || "获取公共库失败");
          }
        })
        .finally(() => {
          this.listLoading = false;
        });
    },
    handleSizeChange(val) {
      this.filters.PageSize = val;
      this.filters.Page = 1;
      this.getList();
    },
    handleCurrentChange(val) {
      this.filters.Page = val;
      this.getList();
    },
    handleUnpublish(row) {
      this.$confirm("下架后组员将无法在公共库查看，确认下架？", "提示", { type: "warning" })
        .then(() => adminUnpublishFromLibrary({ RootDocId: row.RootDocId }))
        .then(res => {
          if (res.success) {
            this.$message.success(res.msg || "已下架");
            this.getList();
          } else {
            this.$message.error(res.msg || "下架失败");
          }
        })
        .catch(() => {});
    },
    handlePublish(row) {
      this.$confirm("确认代发布该教案到公共库？", "提示", { type: "info" })
        .then(() =>
          adminPublishToLibrary({
            RootDocId: row.RootDocId,
            RevisionDocId: row.RevisionDocId
          })
        )
        .then(res => {
          if (res.success) {
            this.$message.success(res.msg || "已发布");
            this.getList();
          } else {
            this.$message.error(res.msg || "发布失败");
          }
        })
        .catch(() => {});
    },
    goDocument(row) {
      this.$router.push({
        path: "/TeachingResearch/PrepDocument",
        query: { docId: row.RevisionDocId || row.RootDocId }
      });
    }
  }
};
</script>

<style scoped>
.prep-library-page .dp-right {
  min-width: 0;
  overflow-x: hidden;
}

.prep-library-page .tr-empty {
  margin: 24px 0 8px;
}
</style>
